/**
 * Typed client for the Kiln HTTP API.
 *
 * Every request carries the session cookie. A 401 from anywhere dispatches
 * `kiln:unauthorized` on window so the shell can drop to the login screen
 * without every call site having to check for it.
 */

export const UNAUTHORIZED_EVENT = 'kiln:unauthorized';

// --- Contract types ---------------------------------------------------------

export type Provider = 'gmail' | 'imap';
export type AuthType = 'app_password' | 'oauth2';
export type AccountStatus =
  | 'pending'
  | 'connecting'
  | 'ok'
  | 'auth_error'
  | 'error'
  | 'disabled';

export interface PublicAccount {
  id: number;
  email: string;
  displayName: string;
  provider: Provider;
  authType: AuthType;
  /** Hex, the account's rail hue as stored on the server. */
  color: string;
  enabled: boolean;
  status: AccountStatus;
  statusDetail: string | null;
  /** Epoch seconds. */
  lastSyncAt: number | null;
  sendAs: string | null;
  signatureHtml: string | null;
  sortOrder: number;
}

export interface Preset {
  key: string;
  provider: Provider;
  imapHost: string;
  imapPort: number;
  smtpHost: string;
  smtpPort: number;
  label: string;
}

export interface AccountInput {
  email: string;
  secret: string;
  displayName?: string;
  preset?: string;
  imapHost?: string;
  imapPort?: number;
  smtpHost?: string;
  smtpPort?: number;
}

export interface AccountPatch {
  displayName?: string;
  color?: string;
  enabled?: boolean;
  sendAs?: string;
  signatureHtml?: string;
  sortOrder?: number;
}

export interface Address {
  name: string;
  address: string;
}

/** Outbound addresses may omit the display name. */
export interface AddressInput {
  name?: string;
  address: string;
}

export type ThreadCategory =
  | 'priority'
  | 'people'
  | 'newsletters'
  | 'notifications'
  | 'receipts';

export type ThreadViewName =
  | 'inbox'
  | 'all'
  | 'unread'
  | 'starred'
  | 'sent'
  | 'archive'
  | 'trash'
  | 'snoozed';

export interface ThreadView {
  threadKey: string;
  subject: string;
  snippet: string;
  participants: Address[];
  /** Drives the account colour rail(s). */
  accountIds: number[];
  messageCount: number;
  unreadCount: number;
  hasAttachments: boolean;
  flagged: boolean;
  pinned: boolean;
  /** Epoch seconds. */
  lastDate: number;
  category: string | null;
  snoozedUntil: number | null;
  aiSummary: string | null;
}

export interface AttachmentView {
  id: number;
  partId: string;
  filename: string;
  mimeType: string;
  size: number;
  contentId: string | null;
  inline: boolean;
}

export interface MessageView {
  id: number;
  accountId: number;
  messageId: string | null;
  subject: string;
  from: Address;
  to: Address[];
  cc: Address[];
  date: number;
  receivedAt: number;
  snippet: string;
  seen: boolean;
  flagged: boolean;
  draft: boolean;
  /** Server-sanitized; still render it sandboxed. */
  bodyHtml: string | null;
  bodyText: string | null;
  attachments: AttachmentView[];
  folders: string[];
}

export type KilnEvent =
  | { type: 'hello'; serverTime: number }
  | { type: 'thread-changed'; threadKey: string }
  | { type: 'threads-changed'; threadKeys: string[]; accountId?: number }
  | { type: 'account-status'; accountId: number; status: string; detail?: string }
  | {
      type: 'sync-progress';
      accountId: number;
      folder: string;
      done: number;
      total: number;
    };

export interface SessionInfo {
  /** Token-hash prefix — enough to identify a device, not to reconstruct it. */
  id: string;
  created_at: number;
  expires_at: number;
  last_seen: number;
  user_agent: string | null;
  ip: string | null;
  label: string | null;
}

export interface MeResponse {
  ok: true;
  aiEnabled: boolean;
  sessions: SessionInfo[];
}

export interface LoginResponse {
  ok: true;
  expiresAt: number;
}

export interface AccountsResponse {
  accounts: PublicAccount[];
  presets: Preset[];
}

export interface TestAccountResponse {
  ok: true;
  folders: number;
}

export interface ThreadsPage {
  threads: ThreadView[];
  nextBefore: number | null;
}

export interface ThreadDetail {
  thread: ThreadView;
  messages: MessageView[];
}

export interface SearchResponse {
  threads: ThreadView[];
  query: string;
}

export interface OutgoingAttachment {
  filename: string;
  contentType: string;
  /** base64, no data: prefix. */
  content: string;
  /** Decoded byte count, for the UI's running total. */
  size: number;
}

/** Decoded budget; base64 pushes ~18MB of files to ~24MB, just under the 25MB most providers cap at. */
export const MAX_ATTACHMENT_BYTES = 18 * 1024 * 1024;

export interface SendPayload {
  accountId: number;
  to: AddressInput[];
  cc?: AddressInput[];
  bcc?: AddressInput[];
  subject: string;
  text?: string;
  html?: string;
  /** The parent MessageView.messageId. */
  inReplyTo?: string | null;
  /** Space-joined chain. */
  references?: string | null;
  threadKey?: string | null;
  attachments?: Omit<OutgoingAttachment, 'size'>[];
}

export interface SendResponse {
  ok: true;
  messageId: string;
}

export interface StatsPerAccount {
  id: number;
  email: string;
  color: string;
  status: AccountStatus;
  messages: number;
  lastSyncAt: number | null;
}

export interface Stats {
  messages: number;
  threads: number;
  unreadThreads: number;
  accounts: number;
  contacts: number;
  perAccount: StatsPerAccount[];
  serverTime: number;
}

export interface ThreadQuery {
  view?: ThreadViewName;
  account?: number | null;
  category?: ThreadCategory | string | null;
  limit?: number;
  before?: number | null;
}

export type ThreadActionName =
  | 'read'
  | 'star'
  | 'archive'
  | 'trash'
  | 'snooze'
  | 'pin';

export interface ThreadActionBody {
  value?: boolean;
  restore?: boolean;
  /** Epoch seconds, or null to un-snooze. */
  until?: number | null;
}

export type AiSummaryMode = 'short' | 'detailed' | 'actions';

export interface ParsedSearch {
  filters: Record<string, string | number | boolean>;
  remainder: string;
}

// --- Errors -----------------------------------------------------------------

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail?: string;
  readonly retryAfterSec?: number;
  readonly issues?: unknown[];

  constructor(
    status: number,
    code: string,
    opts: { detail?: string; retryAfterSec?: number; issues?: unknown[] } = {},
  ) {
    super(opts.detail || code);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = opts.detail;
    this.retryAfterSec = opts.retryAfterSec;
    this.issues = opts.issues;
  }

  /** The string to actually put in front of a person. */
  get displayMessage(): string {
    return this.detail || HUMAN_ERRORS[this.code] || this.code.replace(/_/g, ' ');
  }
}

const HUMAN_ERRORS: Record<string, string> = {
  unauthorized: 'Your session expired. Sign in again.',
  invalid_password: 'That password is not correct.',
  too_many_attempts: 'Too many attempts.',
  already_exists: 'That mailbox is already connected.',
  not_found: 'Not found.',
  bad_request: 'The server rejected that request.',
  action_failed: 'The mail server refused that change.',
  send_failed: 'The message could not be sent.',
  network: 'Cannot reach the Kiln server.',
};

// --- Transport --------------------------------------------------------------

type ErrorBody = {
  error?: string;
  detail?: string;
  retryAfterSec?: number;
  issues?: unknown[];
};

async function request<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, headers, ...rest } = init;
  let res: Response;
  try {
    res = await fetch(path, {
      ...rest,
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        ...(json === undefined ? {} : { 'content-type': 'application/json' }),
        ...headers,
      },
      body: json === undefined ? rest.body : JSON.stringify(json),
    });
  } catch {
    throw new ApiError(0, 'network');
  }

  if (res.status === 204) return undefined as T;

  const isJson = (res.headers.get('content-type') ?? '').includes('application/json');
  const payload: unknown = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const body: ErrorBody =
      isJson && payload !== null && typeof payload === 'object' ? (payload as ErrorBody) : {};
    if (res.status === 401) {
      window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    }
    throw new ApiError(res.status, body.error ?? `http_${res.status}`, {
      detail: body.detail,
      retryAfterSec:
        body.retryAfterSec ?? numberOrUndefined(res.headers.get('retry-after')),
      issues: body.issues,
    });
  }

  return payload as T;
}

function numberOrUndefined(v: string | null): number | undefined {
  if (v === null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function qs(params: Record<string, string | number | null | undefined>): string {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') continue;
    s.set(k, String(v));
  }
  const out = s.toString();
  return out ? `?${out}` : '';
}

/** Thread keys can contain `/` and `+`; they must survive the path segment. */
const encodeKey = (key: string) => encodeURIComponent(key);

// --- Client -----------------------------------------------------------------

export type OAuthProviderId = 'google' | 'microsoft';

export interface OAuthProviderInfo {
  id: OAuthProviderId;
  label: string;
  configured: boolean;
  /** Not secret; shown so the user can confirm they pasted the right client. */
  clientId: string | null;
  /** Microsoft only; null means the multi-tenant `common` authority. */
  tenant: string | null;
  /** Epoch seconds. Microsoft only — Entra client secrets lapse. */
  secretExpiresAt: number | null;
  /**
   * True when this provider's client secret has a hard expiry. When it lapses
   * every account on that provider stops at once, and the fix is a new secret
   * in the provider's console — not a per-account reconnect.
   */
  secretExpires: boolean;
  scopes: string[];
}

export interface OAuthConfig {
  configured: boolean;
  clientId: string | null;
  /** Must match the Authorized redirect URI in the provider's console exactly. */
  redirectUri: string;
  scopes: string[];
  provider: OAuthProviderId;
  tenant: string | null;
  secretExpiresAt: number | null;
  providers: OAuthProviderInfo[];
}

export interface OAuthClientInput {
  provider?: OAuthProviderId;
  clientId: string;
  clientSecret: string;
  tenant?: string;
  secretExpiresAt?: number | null;
}

export const api = {
  login: (password: string, label?: string) =>
    request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      json: { password, ...(label ? { label } : {}) },
    }),

  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  me: () => request<MeResponse>('/api/auth/me'),

  revokeAll: () =>
    request<{ ok: true; revoked: number }>('/api/auth/revoke-all', { method: 'POST' }),

  accounts: () => request<AccountsResponse>('/api/accounts'),

  testAccount: (input: AccountInput) =>
    request<TestAccountResponse>('/api/accounts/test', { method: 'POST', json: input }),

  addAccount: (input: AccountInput) =>
    request<{ account: PublicAccount }>('/api/accounts', { method: 'POST', json: input }),

  updateAccount: (id: number, patch: AccountPatch) =>
    request<{ account: PublicAccount }>(`/api/accounts/${id}`, {
      method: 'PATCH',
      json: patch,
    }),

  reconnectAccount: (id: number, secret: string) =>
    request<{ ok: true }>(`/api/accounts/${id}/reconnect`, {
      method: 'POST',
      json: { secret },
    }),

  deleteAccount: (id: number) =>
    request<{ ok: true }>(`/api/accounts/${id}`, { method: 'DELETE' }),

  threads: (params: ThreadQuery = {}) =>
    request<ThreadsPage>(
      `/api/threads${qs({
        view: params.view,
        account: params.account,
        category: params.category,
        limit: params.limit,
        before: params.before,
      })}`,
    ),

  thread: (key: string) => request<ThreadDetail>(`/api/threads/${encodeKey(key)}`),

  threadAction: (key: string, action: ThreadActionName, body?: ThreadActionBody) =>
    request<{ ok: true }>(`/api/threads/${encodeKey(key)}/${action}`, {
      method: 'POST',
      json: body ?? {},
    }),

  search: (q: string, opts: { account?: number | null; limit?: number } = {}) =>
    request<SearchResponse>(
      `/api/search${qs({ q, account: opts.account, limit: opts.limit })}`,
    ),

  send: (payload: SendPayload) =>
    request<SendResponse>('/api/send', { method: 'POST', json: payload }),

  /** Signs one remote image URL. Only call this after the user opts in. */
  imgsign: (url: string) => request<{ src: string }>(`/api/imgsign${qs({ url })}`),

  stats: () => request<Stats>('/api/stats'),

  /** Same-origin stream; safe to use directly as an href or img src. */
  attachmentUrl: (messageId: number, partId: string) =>
    `/api/attachments/${messageId}/${encodeURIComponent(partId)}`,

  /**
   * Google OAuth onboarding — the preferred way to add a mailbox.
   *
   * The consent flow runs in a popup rather than a redirect so the app keeps
   * its state (and any open draft) while the user is over on accounts.google.com.
   */
  oauth: {
    /** `provider` defaults to google server-side, so omitting it is safe. */
    config: (provider?: OAuthProviderId) =>
      request<OAuthConfig>(`/api/oauth/config${qs({ provider })}`),

    setConfig: (input: OAuthClientInput) =>
      request<{ ok: true; provider: OAuthProviderId; redirectUri: string }>('/api/oauth/config', {
        method: 'POST',
        json: input,
      }),

    /** Returns the provider's consent URL to open in a popup. */
    start: (opts: { provider?: OAuthProviderId; email?: string; accountId?: number } = {}) =>
      request<{ url: string; provider: OAuthProviderId }>(
        `/api/oauth/start${qs({ provider: opts.provider, email: opts.email, accountId: opts.accountId })}`,
      ),
  },

  ai: {
    summarize: (threadKey: string, mode?: AiSummaryMode) =>
      request<{ summary: string }>('/api/ai/summarize', {
        method: 'POST',
        json: { threadKey, ...(mode ? { mode } : {}) },
      }),

    reply: (threadKey: string, instruction?: string) =>
      request<{ draft: string }>('/api/ai/reply', {
        method: 'POST',
        json: { threadKey, ...(instruction ? { instruction } : {}) },
      }),

    chips: (threadKey: string) =>
      request<{ chips: string[] }>('/api/ai/chips', { method: 'POST', json: { threadKey } }),

    parseSearch: (query: string) =>
      request<ParsedSearch>('/api/ai/search', { method: 'POST', json: { query } }),
  },
};

// --- Account hues -----------------------------------------------------------

/**
 * The eight design-system account hues, in order. Used when an account has no
 * colour of its own; the server assigns one at creation time and that value is
 * what every surface — rails, dots, pills — must agree on.
 */
export const ACCOUNT_HUE_VARS = [
  'var(--acct-1)',
  'var(--acct-2)',
  'var(--acct-3)',
  'var(--acct-4)',
  'var(--acct-5)',
  'var(--acct-6)',
  'var(--acct-7)',
  'var(--acct-8)',
] as const;

/**
 * The one place account colour is resolved. Every rail, dot and pill in the app
 * must call this — eight hues already tax hue memory, and the same mailbox
 * wearing two different colours in two panes would make the code unreadable.
 * The server's stored colour wins; the token palette is the fallback, indexed
 * by list position so hues stay distinct after a mailbox is removed.
 */
export function accountHue(accountId: number, accounts: readonly PublicAccount[]): string {
  const index = accounts.findIndex((a) => a.id === accountId);
  if (index < 0) return 'var(--text-quaternary)';
  return accounts[index]?.color || ACCOUNT_HUE_VARS[index % ACCOUNT_HUE_VARS.length]!;
}

export function accountById(
  accountId: number,
  accounts: readonly PublicAccount[],
): PublicAccount | undefined {
  return accounts.find((a) => a.id === accountId);
}

/** Short label for the receiving-account pill: the local part, or the name. */
export function accountLabel(account: PublicAccount): string {
  if (account.displayName && account.displayName !== account.email) {
    return account.displayName;
  }
  return account.email.split('@')[0] ?? account.email;
}
